/**
 * The mobile overlay's navigation.
 *
 * The markup ships fully expanded — every link reachable — and this collapses
 * it on load. That way a panel can never end up sealed shut with its contents
 * unreachable because the script failed, and nothing flashes on screen, because
 * the overlay itself is closed until someone opens it.
 *
 * A parent row has two controls with two jobs:
 *
 * - The LINK opens its level when that level is shut, and follows through to
 *   the parent page when it is already open. State, not timing: a first pass
 *   used a 700ms window for the second tap, which read as "you must double-tap
 *   fast" and failed anyone who opened a level, looked at it, and then decided
 *   they wanted the parent page after all. Now that second tap works whenever
 *   it comes.
 * - The chevron BUTTON toggles, always, and never navigates — it is the only way
 *   to close a level from its own row, and the one a keyboard and a screen
 *   reader use. It carries aria-expanded, because Enter on a link has to mean
 *   "follow the link", and a link that swallowed it would strand anyone not
 *   using a pointer.
 *
 * Depth is not fixed anywhere here. Panels are found by attribute and each one
 * owns the level beneath it, so a third level collapses exactly like a second.
 */

/**
 * The panel a parent link controls.
 *
 * @param {HTMLElement} link The parent link.
 * @return {?HTMLElement} Its panel, or null.
 */
function panelFor( link ) {
	const id = link.getAttribute( 'data-dn-nav-parent' );

	return id ? document.getElementById( id ) : null;
}

/**
 * Open or close one panel.
 *
 * `inert` rather than `hidden`: the panel has to keep its box for the height
 * transition to have anything to animate, while still leaving the tab order and
 * the accessibility tree when it is shut.
 *
 * @param {HTMLElement} panel  The panel.
 * @param {HTMLElement} toggle The button reporting its state.
 * @param {boolean}     open   Whether it should end up open.
 */
function setOpen( panel, toggle, open ) {
	panel.classList.toggle( 'is-open', open );
	panel.inert = ! open;

	if ( toggle ) {
		toggle.setAttribute( 'aria-expanded', open ? 'true' : 'false' );
	}
}

/**
 * Wire one navigation.
 *
 * @param {HTMLElement} nav The nav element.
 */
function initNav( nav ) {
	const panels = Array.from( nav.querySelectorAll( '[data-dn-nav-panel]' ) );

	if ( ! panels.length ) {
		return;
	}

	/**
	 * The toggle button sitting in the same row as a panel's own link.
	 *
	 * Resolved from the panel outwards rather than by looking an id up in a
	 * query, so a nested panel can never pick up an ancestor's button.
	 *
	 * @param {HTMLElement} panel The panel.
	 * @return {?HTMLElement} The button.
	 */
	const toggleFor = ( panel ) => {
		const item = panel.closest( '.dn-mobile-nav__item' );

		return item
			? item.querySelector( ':scope > .dn-mobile-nav__row > .dn-mobile-nav__toggle' )
			: null;
	};

	// Collapse everything. The server sent it open so that this failing leaves a
	// long menu rather than an empty one.
	panels.forEach( ( panel ) => setOpen( panel, toggleFor( panel ), false ) );

	// Only now is the collapsing real, so only now should it animate. Without
	// this the whole menu visibly folds up on load in any browser that gets to
	// paint first.
	window.requestAnimationFrame( () => nav.classList.add( 'is-ready' ) );

	nav.addEventListener( 'click', ( event ) => {
		const toggle = event.target.closest( '.dn-mobile-nav__toggle' );

		if ( toggle && nav.contains( toggle ) ) {
			const panel = document.getElementById(
				toggle.getAttribute( 'aria-controls' )
			);

			if ( panel ) {
				setOpen( panel, toggle, ! panel.classList.contains( 'is-open' ) );
			}

			return;
		}

		const link = event.target.closest( '[data-dn-nav-parent]' );

		if ( ! link || ! nav.contains( link ) ) {
			return;
		}

		// A click with no pointer behind it came from the keyboard — detail is
		// 0 for Enter on a link, and 1 or more for a real tap or click. Let it
		// navigate: the chevron is the keyboard's toggle.
		if ( 0 === event.detail ) {
			return;
		}

		const panel = panelFor( link );

		if ( ! panel ) {
			return;
		}

		// Already open: the children are on screen, so this tap means the parent
		// page. Let the link go, however long it has been since the first tap.
		if ( panel.classList.contains( 'is-open' ) ) {
			return;
		}

		// Shut: this tap opens it, and only opens it — closing is the chevron's.
		event.preventDefault();
		setOpen( panel, toggleFor( panel ), true );
	} );
}

/**
 * Start every navigation on the page.
 */
function init() {
	document.querySelectorAll( '[data-dn-mobile-nav]' ).forEach( initNav );
}

if ( 'loading' === document.readyState ) {
	document.addEventListener( 'DOMContentLoaded', init );
} else {
	init();
}
